import $ from 'jquery';
import Cropper from 'cropperjs';
import Keyboard from '../modules/keyboard';

// ─── Image Search Constants ────────────────────────────────────────────────
const MAX_IMAGE_SEARCH_EDGE = 1024;
const IMAGE_SEARCH_JPEG_QUALITY = 0.8;
const IMAGE_SEARCH_DEFAULT_POLL_TIMEOUT_SECONDS = 20;
const MAX_SEARCH_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_IMAGE_URL_FETCH_BYTES = 10 * 1024 * 1024;    // 10MB
const DEFAULT_IMAGE_SEARCH_FILE_NAME = 'image-search.jpg';
const MAX_ORIGINAL_FILE_NAME_LENGTH = 45;
const MAX_UPLOAD_IMAGE_RETRIES = 2;
const UPLOAD_IMAGE_RETRIES_DELAY_MS = 1000;
const MAX_STATUS_CHECK_RETRIES = 1;
const STATUS_CHECK_RETRY_DELAY_MS = 500;

let isImageSearchRunning = false;
let isImagePreparing = false;
let cropImageObjectUrl = null;

// ─── Error Types ───────────────────────────────────────────────────────────
const ImageSearchErrorCode = Object.freeze({
    UNKNOWN: 100,

    // validation
    INVALID_IMAGE: 101,
    INVALID_IMAGE_DIMENSION: 102,
    IMAGE_TOO_LARGE: 103,

    // processing
    IMAGE_READ_FAILED: 104,
    IMAGE_ENCODE_FAILED: 105,
    CANVAS_NOT_SUPPORTED: 106,

    // upload
    UPLOAD_FAILED: 201,
    MISSING_EVENT_ID: 202,

    // polling
    STATUS_CHECK_FAILED: 301,
    INVALID_JSON_RESPONSE: 302,
    INVALID_RETRY_VALUE: 303,
    INVALID_RETRY_NON_POSITIVE: 304,
    TIMEOUT: 305,

    // url
    INVALID_URL: 401,
    URL_FETCH_FAILED: 402,
    URL_NOT_IMAGE: 403
});

class ImageSearchError extends Error {
    constructor(code, message, details) {
        super(message || `Image search failed, error=${code}`);
        this.code = code;
        this.details = details;
        this.name = 'ImageSearchError';
    }
}

function getImageSearchErrorMessage(err) {
    const code = err && err.code ? err.code : ImageSearchErrorCode.UNKNOWN;
    return `Image search failed, error=${code}`;
}

// ─── Utility Helpers ──────────────────────────────────────────────────────
function imageSearchSleep(ms) {
    return new Promise(function (resolve) {
        setTimeout(resolve, ms);
    });
}

// Decode a File/Blob into something canvas.drawImage() can use.
// Prefer createImageBitmap for performance; fall back to <img> for compatibility.
async function loadImageBitmapOrFallback(sourceFileOrBlob) {
    if (window.createImageBitmap) {
        try {
            return await createImageBitmap(sourceFileOrBlob);
        } catch (err) {
            throw new ImageSearchError(
                ImageSearchErrorCode.IMAGE_READ_FAILED,
                null,
                'createImageBitmap failed'
            );
        }
    }
    const objectUrl = URL.createObjectURL(sourceFileOrBlob);
    return new Promise(function (resolve, reject) {
        const image = new Image();

        function cleanup() {
            image.onload = null;
            image.onerror = null;
            URL.revokeObjectURL(objectUrl);
        }

        image.onload = function () {
            cleanup();
            resolve(image);
        };

        image.onerror = function () {
            cleanup();
            reject(new ImageSearchError(
                ImageSearchErrorCode.IMAGE_READ_FAILED,
                null,
                'image load failed'
            ));
        };

        image.src = objectUrl;
    });
}

/*
 * Preprocess image before upload:
 * - resize the longest edge to <= 1024px
 * - preserve aspect ratio
 * - re-encode as JPEG quality 0.8
 * - strip EXIF metadata by redrawing via canvas
 * - reject if processed file is still > 5MB
 */
async function prepareImageForUpload(sourceFileOrBlob, onErrorMessage) {
    function showMessage(msg) {
        if (typeof onErrorMessage === 'function') {
            onErrorMessage(msg);
        } else {
            console.log(msg);
        }
    }

    if (!sourceFileOrBlob || !sourceFileOrBlob.type || !sourceFileOrBlob.type.startsWith('image/')) {
        showMessage('Please choose a valid image.');
        return null;
    }

    let img = null;

    try {
        img = await loadImageBitmapOrFallback(sourceFileOrBlob);

        let width = img.width;
        let height = img.height;

        if (!width || !height) {
            showMessage('Invalid image dimensions.');
            return null;
        }

        const longestEdge = Math.max(width, height);
        if (longestEdge > MAX_IMAGE_SEARCH_EDGE) {
            const scale = MAX_IMAGE_SEARCH_EDGE / longestEdge;
            width = Math.max(1, Math.round(width * scale));
            height = Math.max(1, Math.round(height * scale));
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            showMessage('Browser does not support image processing.');
            return null;
        }

        try {
            ctx.drawImage(img, 0, 0, width, height);
        } catch (err) {
            showMessage(`Image search failed, error=${ImageSearchErrorCode.IMAGE_READ_FAILED}`);
            return null;
        }

        let jpegBlob;
        try {
            jpegBlob = await new Promise(function (resolve, reject) {
                try {
                    canvas.toBlob(function (blob) {
                        if (!blob) {
                            reject(new ImageSearchError(
                                ImageSearchErrorCode.IMAGE_ENCODE_FAILED,
                                null,
                                'canvas.toBlob returned null'
                            ));
                            return;
                        }
                        resolve(blob);
                    }, 'image/jpeg', IMAGE_SEARCH_JPEG_QUALITY);
                } catch (err) {
                    reject(new ImageSearchError(
                        ImageSearchErrorCode.IMAGE_ENCODE_FAILED,
                        null,
                        'canvas.toBlob threw exception'
                    ));
                }
            });
        } catch (err) {
            showMessage(err && err.message
                ? err.message
                : `Image search failed, error=${ImageSearchErrorCode.IMAGE_ENCODE_FAILED}`);
            return null;
        }

        if (jpegBlob.size > MAX_SEARCH_IMAGE_UPLOAD_BYTES) {
            showMessage('Image is still larger than 5MB after processing. Please choose a smaller image.');
            return null;
        }

        return jpegBlob;
    } catch (err) {
        showMessage(err && err.message
            ? err.message
            : `Image search failed, error=${ImageSearchErrorCode.IMAGE_READ_FAILED}`);
        return null;
    } finally {
        if (img && typeof img.close === 'function') {
            try {
                img.close();
            } catch (e) {
                // no-op
            }
        }
    }
}

async function setupImageForCropping(jpegBlob, onErrorMessage) {
    if (!jpegBlob) {
        return false;
    }

    function showMessage(msg) {
        if (typeof onErrorMessage === 'function') {
            onErrorMessage(msg);
        } else {
            console.log(msg);
        }
    }

    try {
        const cropImg = $('#js-visual-search-image')[0];

        if (!cropImg) {
            showMessage('Image processing failed. Please try another image.');
            return false;
        }

        if (cropImageObjectUrl) {
            URL.revokeObjectURL(cropImageObjectUrl);
            cropImageObjectUrl = null;
        }

        cropImageObjectUrl = URL.createObjectURL(jpegBlob);

        function loadImage(img, url) {
            return new Promise((resolve, reject) => {
                img.onload = function () {
                    img.onload = null;
                    img.onerror = null;
                    resolve();
                };
                img.onerror = function () {
                    img.onload = null;
                    img.onerror = null;
                    reject(new ImageSearchError(
                        ImageSearchErrorCode.IMAGE_READ_FAILED,
                        null,
                        'crop image load failed'
                    ));
                };
                img.src = url;
            });
        }

        await loadImage(cropImg, cropImageObjectUrl);
        return true;
    } catch (err) {
        showMessage('Image processing failed. Please try another image.');
        return false;
    }
}

function buildImageSearchFormData(file, originalFileName) {
    const formData = new FormData();
    formData.append('image', file, file.name || DEFAULT_IMAGE_SEARCH_FILE_NAME);
    if (originalFileName) {
        formData.append('originalFileName', originalFileName);
    }
    return formData;
}

async function uploadPreparedImage(file, originalFileName) {
    for (let attempt = 0; attempt <= MAX_UPLOAD_IMAGE_RETRIES; attempt++) {
        let response;
        try {
            response = await fetch('/content/image-search/upload', {
                method: 'POST',
                body: buildImageSearchFormData(file, originalFileName)
            });
        } catch (err) {
            if (attempt < MAX_UPLOAD_IMAGE_RETRIES) {
                await imageSearchSleep(UPLOAD_IMAGE_RETRIES_DELAY_MS * (attempt + 1));
                continue;
            }
            throw new ImageSearchError(
                ImageSearchErrorCode.UPLOAD_FAILED,
                null,
                `fetch failed after ${MAX_UPLOAD_IMAGE_RETRIES + 1} attempts, name=${err && err.name}, message=${err && err.message}, online=${navigator.onLine}`
            );
        }

        if (!response.ok) {
            throw new ImageSearchError(
                ImageSearchErrorCode.UPLOAD_FAILED,
                null,
                `status=${response.status}`
            );
        }

        try {
            return await response.json();
        } catch (err) {
            throw new ImageSearchError(
                ImageSearchErrorCode.INVALID_JSON_RESPONSE,
                null,
                'Invalid JSON in upload response'
            );
        }
    }
}

// Poll backend for image search status.
// Backend may either:
// 1) keep returning retryAfterSeconds, or
// 2) return a redirectUrl when search results are ready.
async function checkImageSearchStatus(eventId) {
    for (let attempt = 0; attempt <= MAX_STATUS_CHECK_RETRIES; attempt++) {
        let res;
        try {
            res = await fetch('/content/image-search/check-status?eventId='
                + encodeURIComponent(eventId), {
                cache: 'no-store'
            });
        } catch (err) {
            if (attempt < MAX_STATUS_CHECK_RETRIES) {
                await imageSearchSleep(STATUS_CHECK_RETRY_DELAY_MS);
                continue;
            }
            throw new ImageSearchError(
                ImageSearchErrorCode.STATUS_CHECK_FAILED,
                null,
                `fetch failed after ${MAX_STATUS_CHECK_RETRIES + 1} attempts, name=${err && err.name}, message=${err && err.message}, online=${navigator.onLine}`
            );
        }

        if (!res.ok) {
            throw new ImageSearchError(
                ImageSearchErrorCode.STATUS_CHECK_FAILED,
                null,
                `status=${res.status}`
            );
        }

        try {
            return await res.json();
        } catch (e) {
            throw new ImageSearchError(
                ImageSearchErrorCode.INVALID_JSON_RESPONSE,
                null,
                'Invalid JSON in check-status response'
            );
        }
    }
}

function normalizeRetryAfterSeconds(value) {
    const normalized = typeof value === 'string' ? Number($.trim(value)) : value;

    if (typeof normalized !== 'number' || isNaN(normalized)) {
        throw new ImageSearchError(
            ImageSearchErrorCode.INVALID_RETRY_VALUE,
            null,
            `waitSeconds=${value}`
        );
    }

    if (normalized <= 0) {
        throw new ImageSearchError(
            ImageSearchErrorCode.INVALID_RETRY_NON_POSITIVE,
            null,
            `waitSeconds=${value}`
        );
    }

    return normalized;
}

// Keep polling until backend returns redirectUrl, an error occurs, or timeout is reached.
async function pollImageSearchResult(uploadResponse) {
    const eventId = uploadResponse && uploadResponse.eventId;
    const timeoutSeconds = uploadResponse && uploadResponse.timeout;
    let retryAfterSeconds = uploadResponse && uploadResponse.retryAfterSeconds;

    if (!eventId) {
        throw new ImageSearchError(
            ImageSearchErrorCode.MISSING_EVENT_ID,
            null,
            'No event id in polling image.'
        );
    }

    const effectiveTimeout = typeof timeoutSeconds === 'number' && timeoutSeconds > 0
        ? timeoutSeconds
        : IMAGE_SEARCH_DEFAULT_POLL_TIMEOUT_SECONDS;
    const deadline = Date.now() + (effectiveTimeout * 1000);

    while (true) {
        if (Date.now() > deadline) {
            return { timedOut: true };
        }

        const waitSeconds = retryAfterSeconds == null
            ? 3
            : normalizeRetryAfterSeconds(retryAfterSeconds);
        const remainingMs = deadline - Date.now();
        if (remainingMs <= 0) {
            return { timedOut: true };
        }
        await imageSearchSleep(Math.min(waitSeconds * 1000, remainingMs));

        const checkResponse = await checkImageSearchStatus(eventId);

        if (checkResponse && checkResponse.redirectUrl) {
            return checkResponse;
        }

        if (checkResponse && checkResponse.errorMessage) {
            throw new ImageSearchError(
                ImageSearchErrorCode.STATUS_CHECK_FAILED,
                null,
                `backendError=${checkResponse.errorMessage}`
            );
        }

        retryAfterSeconds = checkResponse && checkResponse.retryAfterSeconds != null
            ? checkResponse.retryAfterSeconds
            : waitSeconds;
    }
}

// Shared entry point after image has already been preprocessed.
// Upload -> show loading state -> poll until redirect or timeout.
async function startImageSearch(processedFile, onError, originalFileName) {
    if (isImageSearchRunning) {
        return;
    }

    isImageSearchRunning = true;

    try {
        const uploadResponse = await uploadPreparedImage(processedFile, originalFileName);
        if (!uploadResponse || !uploadResponse.eventId) {
            const errorDetails = !uploadResponse ? 'no uploadResponse' : 'no event id';
            onError(`Image search failed, error=${ImageSearchErrorCode.UPLOAD_FAILED}`, errorDetails);
            return;
        }

        const finalResponse = await pollImageSearchResult(uploadResponse);

        if (finalResponse && finalResponse.redirectUrl) {
            window.location.href = finalResponse.redirectUrl;
            return;
        }

        if (finalResponse && finalResponse.timedOut) {
            onError(`Image search failed, error=${ImageSearchErrorCode.TIMEOUT}`);
            return;
        }

        onError(`Image search failed, error=${ImageSearchErrorCode.UNKNOWN}`);
    } catch (err) {
        console.error('Image search failed:', err);
        onError(
            getImageSearchErrorMessage(err),
            err && err.details
        );
    } finally {
        isImageSearchRunning = false;
    }
}

function imageSearchEngagementTracking(isUrlFetch, error) {
    if (typeof isUrlFetch !== 'boolean') {
        console.warn('imageSearchEngagementTracking: isUrlFetch must be a boolean');
        return;
    }

    const trackingData = {
        url: window.location.href,
        isUrlFetch: isUrlFetch,
        ...(error && { error: error instanceof Error ? error.message : `${error}` })
    };

    $.ajax({
        type: 'GET',
        url: '/content/image-search/engagement-tracking',
        data: trackingData,
        timeout: 5000,
        error(jqXHR, textStatus, errorThrown) {
            console.warn('imageSearchEngagementTracking: request failed', { textStatus, errorThrown });
        }
    });
}

function extractFileNameFromUrl(url) {
    try {
        const pathname = new URL(url).pathname;
        let name = pathname.split('/').pop();
        name = name.split('?')[0].split('#')[0];
        name = decodeURIComponent(name);
        if (!name || name.trim() === '') {
            return DEFAULT_IMAGE_SEARCH_FILE_NAME;
        }
        return name;
    } catch {
        return DEFAULT_IMAGE_SEARCH_FILE_NAME;
    }
}

function sanitizeOriginalFileName(name) {
    if (typeof name !== 'string' || !name.trim()) {
        return DEFAULT_IMAGE_SEARCH_FILE_NAME;
    }

    name = name.trim().replace(/[\r\n\t]/g, ' ');

    if (!name) {
        return DEFAULT_IMAGE_SEARCH_FILE_NAME;
    }

    if (name.length <= MAX_ORIGINAL_FILE_NAME_LENGTH) {
        return name;
    }

    const dotIndex = name.lastIndexOf('.');
    const hasExt = dotIndex > 0 && dotIndex < name.length - 1;
    const ext = hasExt ? name.substring(dotIndex) : '';
    const allowedNameLength = MAX_ORIGINAL_FILE_NAME_LENGTH - ext.length;

    if (allowedNameLength <= 0) {
        return name.substring(0, MAX_ORIGINAL_FILE_NAME_LENGTH);
    }

    return name.substring(0, allowedNameLength) + ext;
}

function disableCroppedSubmitButton($button) {
    $button.prop('disabled', true)
        .addClass('disabled')
        .attr('aria-disabled', 'true');
}

function enableCroppedSubmitButton($button) {
    $button.prop('disabled', false)
        .removeClass('disabled')
        .attr('aria-disabled', 'false');
}

// ─── Cropper (desktop) — kept private via IIFE ────────────────────────────
(function () {
    let cropper = null;

    function initVisualSearchCropper(imageEl) {
        if (!imageEl) return;

        if (cropper) {
            cropper.destroy();
            cropper = null;
        }

        window.VisualSearchCropper._currentBlob = null;

        cropper = new Cropper(imageEl, {
            center: false,
            dashed: false,
            guides: false,
            responsive: true,
            rotatable: false,
            scalable: false,
            viewMode: 1,
            zoomable: false,
            zoomOnWheel: false,

            ready() {
                const $cropSubmit = $('#js-visual-search-crop-submit');
                enableCroppedSubmitButton($cropSubmit);

                getCroppedBlob()
                    .then(function (blob) {
                        window.VisualSearchCropper._currentBlob = blob;
                    })
                    .catch(function (err) {
                        console.error('Failed to get cropped image:', err);
                    });
            },

            cropmove(e) {
                const action = e.detail && e.detail.action;
                const cropperEl = cropper && cropper.cropper;
                if (!cropperEl) return;

                if (action === 'ne') {
                    const point = cropperEl.querySelector('.cropper-point.point-ne');
                    if (point) point.classList.add('is-engaged');
                } else if (action === 'sw') {
                    const point = cropperEl.querySelector('.cropper-point.point-sw');
                    if (point) point.classList.add('is-engaged');
                }
            },

            cropend() {
                const cropperEl = cropper && cropper.cropper;
                if (cropperEl) {
                    const nePoint = cropperEl.querySelector('.cropper-point.point-ne');
                    const swPoint = cropperEl.querySelector('.cropper-point.point-sw');
                    if (nePoint) nePoint.classList.remove('is-engaged');
                    if (swPoint) swPoint.classList.remove('is-engaged');
                }

                getCroppedBlob()
                    .then(function (blob) {
                        if (blob) {
                            window.VisualSearchCropper._currentBlob = blob;
                        }
                    })
                    .catch(function (err) {
                        console.error('Failed to get cropped image:', err);
                    });
            }
        });

        return cropper;
    }

    function getCroppedBlob() {
        if (!cropper) {
            return Promise.resolve(null);
        }

        const canvas = cropper.getCroppedCanvas({
            maxWidth: MAX_IMAGE_SEARCH_EDGE,
            maxHeight: MAX_IMAGE_SEARCH_EDGE,
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'high'
        });

        if (!canvas) {
            return Promise.resolve(null);
        }

        return new Promise((resolve, reject) => {
            canvas.toBlob(function (blob) {
                if (!blob) {
                    reject(new ImageSearchError(
                        ImageSearchErrorCode.IMAGE_ENCODE_FAILED,
                        null,
                        'crop canvas.toBlob returned null'
                    ));
                    return;
                }
                resolve(blob);
            }, 'image/jpeg', IMAGE_SEARCH_JPEG_QUALITY);
        });
    }

    function destroyCropper() {
        if (cropper) {
            cropper.destroy();
            cropper = null;
        }

        if (cropImageObjectUrl) {
            URL.revokeObjectURL(cropImageObjectUrl);
            cropImageObjectUrl = null;
        }

        window.VisualSearchCropper._currentBlob = null;
    }

    window.VisualSearchCropper = {
        init: initVisualSearchCropper,
        getBlob: getCroppedBlob,
        destroy: destroyCropper,
    };
})();

$(() => {
    if ($('.ab553').length) {
        const $visualSearchButton = $('.js-visual-search-button');
        const $siteSearchResults = $('.site-search__results');
        const $visualSearchPanel = $('#visual-search-panel');
        const $visualSearchDropZone = $('.site-search__visual-dropzone');
        const $visualSearchStatus = $('#visual-search-status');
        const VS_PANEL_OPEN_STATE_CLASS = 'site-search__visual--open';
        const $fileInputUpload = $('#visual-search-photo-upload');
        const $urlInputField = $('.site-search__visual-paste');
        const $urlInputUpload = $('#visual-search-image-url');
        const $uploadButton = $('#visual-search-upload-button');
        const $allDropZoneElements = $('.site-search__visual-dropzone-content');
        const $urlSubmitButton = $('#visual-search-url-submit');
        const $cropState = $('.site-search__visual-crop');
        const $legalCopy = $('#image-upload-legal-copy');
        const $cropCanvas = $('.site-search__visual-crop__canvas');
        const $cropImage = $('#js-visual-search-image');
        const $cropSubmit = $('#js-visual-search-crop-submit');
        const $cropError            = $('#crop-img-error');

        // Track filename across async steps
        let pendingOriginalFileName = null;

        // ── bfcache cleanup (iOS/Safari back-swipe) ──────────────────────────
        window.addEventListener('pagehide', function () {
            closeVisualSearchPanel();
            if (window.VisualSearchCropper) {
                window.VisualSearchCropper.destroy();
            }
        });

        // ── Panel open/close ─────────────────────────────────────────────────
        $(document).on('mousedown.visualSearch', function (event) {
            if (!$visualSearchPanel.hasClass(VS_PANEL_OPEN_STATE_CLASS)) return;
            
            const clickedOutsidePanel =
                !$visualSearchPanel.is(event.target) && 
                $visualSearchPanel.has(event.target).length === 0;
            
            const clickedButton = 
                $visualSearchButton.is(event.target) || 
                $visualSearchButton.has(event.target).length > 0;
            
            if (clickedOutsidePanel && !clickedButton) {
                closeVisualSearchPanel();
            }
        });
    
        $visualSearchButton.on('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            $siteSearchResults.hide();
            const isOpen = $visualSearchPanel.hasClass(VS_PANEL_OPEN_STATE_CLASS);
            if (isOpen) {
                closeVisualSearchPanel();
            } else {
                openVisualSearchPanel();
            }
            if (typeof logEngagement === 'function') {
                logEngagement(window.location.href, 'IMAGE_SEARCH', 'camera icon clicked');
            }
        });

        // ── First-look coachmark (UX-21255) ──────────────────────────────────
        initVisualSearchCoachmark();

        /**
         * Points a small callout at the camera icon so shoppers discover visual
         * search. Desktop shows it 1.5s after load. Tablet (below 1001px) hides
         * the camera until the search field slides open, so there it models
         * mobile: it is scheduled when the field opens (modules/header.js fires
         * visualSearch:tabletOpen) and the field's auto-focus is held back while
         * the coachmark is due. Closes on intent (focus into search, hover/click
         * on another header item, Esc, field collapsing) and comes
         * back on later page loads up to MAX_SHOWS times. The X, the CTA and the
         * camera icon itself all dismiss it for good. State lives in localStorage.
         */
        function initVisualSearchCoachmark() {
            const $coachmark = $('#visual-search-coachmark');
            const $searchInput = $('#search_term_new');
            const $header = $('.site-header');
            const $abContainer = $visualSearchButton.closest('.ab553');
            const STORAGE_KEY = 'revolveVisualSearchCoachmark';
            const MAX_SHOWS = 3;
            const SHOW_DELAY_MS = 1500;
            const HIDE_DURATION_MS = 500; // matches the CSS transition
            const ARROW_OFFSET_PX = 87;   // arrow centre from the box's leading edge, per Figma
            const VIEWPORT_GUTTER_PX = 16; // keep the box inside the viewport on narrow (tablet) widths
            const VISIBLE_CLASS = 'is-visible';
            const NS = '.vsCoachmark';
            let showTimer = null;
            let hideTimer = null;
            // The lifetime cap counts visits, not tablet search opens: once per page load at most.
            let shownThisPageLoad = false;

            if (!$coachmark.length) return;
            // Suppressed on PDP per spec. Checkout never renders this header.
            if ($('body').hasClass('page--product-detail')) return;

            // Camera hidden at load means we are on tablet and must wait for the field to open.
            const isTablet = !$visualSearchButton.is(':visible');

            $coachmark.on('click', '.js-visual-search-coachmark-close', function () {
                dismiss('coachmark dismissed');
                $visualSearchButton.focus();
            });

            $coachmark.on('click', '.js-visual-search-coachmark-cta', function () {
                dismiss('coachmark cta clicked');
                $siteSearchResults.hide();
                openVisualSearchPanel();
            });

            // header.js collapses the tablet field on any document click outside the
            // input; clicks inside the coachmark must not count as "outside".
            $coachmark.on('click', function (e) {
                e.stopPropagation();
            });

            if (isTablet) {
                $abContainer.on('visualSearch:tabletOpen', function (ev) {
                    if (!isEligible()) return;
                    ev.preventDefault(); // hold back auto-focus until the coachmark is dismissed
                    clearTimeout(showTimer);
                    showTimer = setTimeout(show, SHOW_DELAY_MS);
                });
                $abContainer.on('visualSearch:tabletClose', function () {
                    clearTimeout(showTimer);
                    hide();
                });
            } else {
                if (!isEligible()) return;
                showTimer = setTimeout(show, SHOW_DELAY_MS);
            }

            // Not permanently dismissed and under the lifetime cap.
            function isEligible() {
                if (shownThisPageLoad) return false;
                const state = readState();
                return !state.dismissed && state.shown < MAX_SHOWS;
            }

            function readState() {
                try {
                    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
                    return {
                        shown: Number(parsed.shown) || 0,
                        dismissed: Boolean(parsed.dismissed),
                    };
                } catch {
                    return { shown: 0, dismissed: false };
                }
            }

            function writeState(next) {
                try {
                    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
                } catch {
                    // Storage unavailable (private mode, quota). Coachmark still works for this page view.
                }
            }

            function log(action) {
                if (typeof logEngagement === 'function') {
                    logEngagement(window.location.href, 'IMAGE_SEARCH', action);
                }
            }

            // The button and the coachmark share .site-search__field as offsetParent,
            // so the button's offset centre maps straight onto the coachmark's left.
            // The box is then clamped to the viewport (tablet can push it past the
            // right edge) and the arrow re-aimed at the camera from wherever it lands.
            function position() {
                const btn = $visualSearchButton[0];
                const parent = btn.offsetParent || $abContainer[0];
                const parentLeft = parent.getBoundingClientRect().left;
                const boxWidth = $coachmark.outerWidth();
                const btnCentre = btn.offsetLeft + (btn.offsetWidth / 2);
                const isRtl = window.getComputedStyle(btn).direction === 'rtl';

                let left = isRtl
                    ? btnCentre - (boxWidth - ARROW_OFFSET_PX)
                    : btnCentre - ARROW_OFFSET_PX;

                const minLeft = VIEWPORT_GUTTER_PX - parentLeft;
                const maxLeft = window.innerWidth - VIEWPORT_GUTTER_PX - parentLeft - boxWidth;
                left = Math.max(minLeft, Math.min(left, maxLeft));

                $coachmark.css({ left, right: 'auto' });
                $coachmark[0].style.setProperty('--vs-coachmark-arrow', `${btnCentre - left}px`);
            }

            function show() {
                if (!$visualSearchButton.is(':visible')) return;
                if ($visualSearchPanel.hasClass(VS_PANEL_OPEN_STATE_CLASS)) return;
                if ($searchInput.is(':focus')) return;

                shownThisPageLoad = true;
                writeState({ shown: readState().shown + 1, dismissed: false });
                $coachmark.prop('hidden', false);
                position();
                // Two frames after leaving display:none so every engine (Safari
                // in particular) paints the hidden state first and runs the transition.
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        $coachmark.addClass(VISIBLE_CLASS);
                    });
                });
                bindCloseOnIntent();
                log('coachmark shown');
            }

            function hide() {
                unbindCloseOnIntent();
                $coachmark.removeClass(VISIBLE_CLASS);
                clearTimeout(hideTimer);
                hideTimer = setTimeout(() => $coachmark.prop('hidden', true), HIDE_DURATION_MS);
            }

            function dismiss(action) {
                writeState({ shown: readState().shown, dismissed: true });
                hide();
                if (action) log(action);
            }

            function bindCloseOnIntent() {
                // Scrolling deliberately does not close it (design review 2026-09-21): the
                // header is pinned on scroll, so the coachmark stays in view with the field.
                $(window).on(`resize${NS}`, position);
                $searchInput.on(`focus${NS}`, hide);
                $(document).on(`keydown${NS}`, function (e) {
                    if (e.key === 'Escape') hide();
                });
                $header.on(`mouseenter${NS} click${NS}`, 'a, button', function (e) {
                    const $item = $(this);
                    if ($item.closest($coachmark).length) return;
                    if ($item.is($visualSearchButton)) {
                        // Hovering toward the camera shouldn't close it; clicking it means they found the feature.
                        if (e.type === 'click') dismiss();
                        return;
                    }
                    hide();
                });
            }

            function unbindCloseOnIntent() {
                $(window).off(NS);
                $searchInput.off(NS);
                $(document).off(NS);
                $header.off(NS);
            }
        }

        // ── Keyboard trap ────────────────────────────────────────────────────
        $visualSearchPanel.on('keydown', function (e) {
            if (e.key !== 'Tab') return;
        
            const $focusable = Keyboard.findFocusable($visualSearchPanel);
            const $first = $focusable.eq(0);
            const $last = $focusable.eq(-1);

            if (e.target === $last[0] && !e.shiftKey) {
                closeVisualSearchPanel();
            }

            if (e.target === $first[0] && e.shiftKey) {
                e.preventDefault();
                closeVisualSearchPanel();
            }
        });
    
        $visualSearchPanel.on('keydown', function (e) {
            if (e.key === 'Escape') {
                e.stopPropagation();
                closeVisualSearchPanel();
            }
        });

        // ── Drag & Drop ───────────────────────────────────────────────────────
        $visualSearchDropZone.on('dragover dragenter', function (e) {
            e.preventDefault();
            e.stopPropagation();
            $visualSearchDropZone.removeClass('is-error');
            $(this).addClass('is-dragover');
        });
    
        $visualSearchDropZone.on('dragleave dragend drop', function (e) {
            e.preventDefault();
            e.stopPropagation();
            $(this).removeClass('is-dragover');
        });
    
        $visualSearchDropZone.on('drop', async function (e) {
            const files = e.originalEvent.dataTransfer.files;
            if (files.length) {
                await handleFileUpload(files[0]);
            }
        });

        // ── File input ────────────────────────────────────────────────────────
        $fileInputUpload.on('change', async function() {
            const file = this.files && this.files[0];
            $(this).val(''); // allow re-selecting the same file
            if (file) {
                await handleFileUpload(file);
            }
        });
    
        $uploadButton.on('click', (e) => {
            e.preventDefault();
            $fileInputUpload.click();
        });

        // ── URL input ─────────────────────────────────────────────────────────
        $urlSubmitButton.on('click', async function () {
            const value = $.trim($urlInputUpload.val());
            if (!value || $urlSubmitButton.prop('disabled')) return;
            await handleUrlUpload(value);
        });
    
        $urlInputUpload.on('input', function () {
            const hasValue = $.trim($(this).val()).length > 0;
            setSubmitDisabled(!hasValue);
            if (hasValue) {
                $urlInputField.removeClass('is-error');
                $urlInputUpload.attr('aria-invalid', 'false');
            }
        });
    
        $urlInputUpload.on('keydown', async function (e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                const value = $.trim($(this).val());
                if (!value || $urlSubmitButton.prop('disabled')) return;
                await handleUrlUpload(value);
            }
        });
    
        $urlInputUpload.on('paste', async function (e) {
            const clipboard = e.originalEvent.clipboardData;
            if (!clipboard) return;
            
            for (let i = 0; i < clipboard.items.length; i++) {
                const item = clipboard.items[i];
                if (item.kind === 'file' && item.type.indexOf('image/') === 0) {
                    e.preventDefault();
                    await handleFileUpload(item.getAsFile());
                    return;
                }
            }
    
            const input = this;
            // During paste event, value is usually not inserted, so we have to wait
            setTimeout(function () {
                const url = $.trim($(input).val());
                if (!url) return;

                handleUrlUpload(url);
            }, 0);
        });

        // ── Crop submit ───────────────────────────────────────────────────────
        $cropSubmit.on('click', async function() {
            if ($(this).prop('disabled')) return;
            if (!window.VisualSearchCropper) return;

            disableCroppedSubmitButton($cropSubmit);
            $cropError.text('').hide();
            $cropState.addClass('site-search__visual-crop--searching');
            $visualSearchStatus.text('searching');
            
            let blob = null;
            try {
                blob = await window.VisualSearchCropper.getBlob();
            } catch (err) {
                console.error('Failed to get cropped image:', err);
            }

            if (!blob) {
                $cropError.text('Failed to get cropped image. Please try again.').show();
                $cropState.removeClass('site-search__visual-crop--searching');
                enableCroppedSubmitButton($cropSubmit);
                return;
            }

            try {
                await startImageSearch(blob, showImageSearchError, pendingOriginalFileName);
            } catch (err) {
                console.error('Image upload failed:', err);
                showImageSearchError(
                    getImageSearchErrorMessage(err),
                    err && err.details
                );
            } finally {
                $cropState.removeClass('site-search__visual-crop--searching');
                enableCroppedSubmitButton($cropSubmit);
            }
        });

        // ── Crop cancel ───────────────────────────────────────────────────────
        $('.js-visual-search-crop-cancel').on('click', function () {
            resetDropZoneUI();
        });

        // ─────────────────────────────────────────────────────────────────────
        // Internal helpers
        // ─────────────────────────────────────────────────────────────────────
        
        function setSubmitDisabled(isDisabled) {
            $urlSubmitButton.prop('disabled', isDisabled);
        }

        async function handleFileUpload(file) {
            try {
                if (!file.type.startsWith('image/')) {
                    showDropZoneError('Please drop an image file.', 'Unsupported File Type');
                    return;
                }

                if (isImagePreparing || isImageSearchRunning) {
                    $visualSearchStatus.text('Image search is already in progress.');
                    return;
                }
                isImagePreparing = true;

                const processedFile = await prepareImageForUpload(file, function (msg) {
                    showDropZoneError(msg);
                });

                if (!processedFile) return;

                const cropImageReady = await setupImageForCropping(processedFile, function (msg) {
                    showDropZoneError(msg);
                });

                if (cropImageReady) {
                    if (!$visualSearchPanel.hasClass(VS_PANEL_OPEN_STATE_CLASS)) {
                        return;
                    }
                    imageSearchEngagementTracking(false);
                    pendingOriginalFileName = sanitizeOriginalFileName(file.name);
                    startCropUI();
                }
            } catch (err) {
                console.error('Image upload failed:', err);
                showDropZoneError(getImageSearchErrorMessage(err));
                imageSearchEngagementTracking(false, err);
            } finally {
                isImagePreparing = false;
            }
        }

        async function handleUrlUpload(url) {
            if ($visualSearchDropZone.hasClass('is-processing')) return;

            const normalized = normalizeUrl(url);
            if (!isValidUrl(normalized)) {
                $urlInputField.addClass('is-error');
                $urlInputUpload.attr('aria-invalid', 'true');
                setSubmitDisabled(true);
                return;
            }

            if (isImagePreparing || isImageSearchRunning) {
                $visualSearchStatus.text('Image search is already in progress.');
                return;
            }
            isImagePreparing = true;

            setSubmitDisabled(true);
            $allDropZoneElements.hide();
            $visualSearchDropZone.addClass('is-processing');

            const unsupportedUrlMessage = "This image URL is not supported. Please download it first and upload it using the upload button instead.";

            try {
                const controller = new AbortController();
                const timer = setTimeout(() => controller.abort(), 10000);

                let imageResponse;
                try {
                    imageResponse = await fetch(normalized, { signal: controller.signal });
                } finally {
                    clearTimeout(timer);
                }

                if (!imageResponse.ok) {
                    showDropZoneError(unsupportedUrlMessage);
                    return;
                }

                const contentType = imageResponse.headers.get('content-type') || '';
                if (!contentType.startsWith('image/')) {
                    showDropZoneError('The provided URL does not point to a valid image.');
                    return;
                }

                const contentLength = imageResponse.headers.get('content-length');
                if (contentLength && parseInt(contentLength, 10) > MAX_IMAGE_URL_FETCH_BYTES) {
                    showDropZoneError('Image is too large. Please choose a smaller image.');
                    return;
                }

                const blob = await imageResponse.blob();
                if (blob.size > MAX_IMAGE_URL_FETCH_BYTES) {
                    showDropZoneError('Image is too large. Please choose a smaller image.');
                    return;
                }

                const processedFile = await prepareImageForUpload(blob, function (msg) {
                    showDropZoneError(msg);
                });

                if (!processedFile) return;

                const cropImageReady = await setupImageForCropping(processedFile, function (msg) {
                    showDropZoneError(msg);
                });

                if (cropImageReady) {
                    if (!$visualSearchPanel.hasClass(VS_PANEL_OPEN_STATE_CLASS)) {
                        return;
                    }
                    imageSearchEngagementTracking(true);
                    const extractedName = extractFileNameFromUrl(normalized);
                    pendingOriginalFileName = sanitizeOriginalFileName(extractedName);
                    startCropUI();
                }
            } catch (err) {
                console.error('Image URL upload failed:', err);
                if (err && err.name === 'AbortError') {
                    showDropZoneError('Request timed out. Please try again.');
                } else {
                    showDropZoneError(unsupportedUrlMessage);
                }
                imageSearchEngagementTracking(true, err);
            } finally {
                isImagePreparing = false;
                $visualSearchDropZone.removeClass('is-processing');
                setSubmitDisabled(false);
            }
        }

        // Transition from drop-zone view → crop view
        function startCropUI() {
            $allDropZoneElements.hide();
            $visualSearchDropZone.hide();
            $urlInputField.hide();
            $cropError.text('').hide();

            $cropState.removeClass('u-hide site-search__visual-crop--searching');
            $legalCopy.hide();

            const MAX_CANVAS_H = 450;
            const panelWidth = $cropCanvas[0].offsetWidth;
            const image = $cropImage[0];

            function initCropperWhenReady() {
                const naturalRatio = image.naturalHeight / image.naturalWidth;
                const computedH = Math.round(panelWidth * naturalRatio);
                $cropCanvas.css({ height: Math.min(computedH, MAX_CANVAS_H) + 'px', width: '' });

                disableCroppedSubmitButton($cropSubmit);
                window.VisualSearchCropper.init(image);
            }

            if (image.complete && image.naturalHeight !== 0) {
                initCropperWhenReady();
            } else {
                image.onload = function () {
                    image.onload = null;
                    initCropperWhenReady();
                };
            }
        }
    
        function openVisualSearchPanel() {
            $visualSearchPanel.addClass(VS_PANEL_OPEN_STATE_CLASS);
            $visualSearchButton.attr('aria-expanded', 'true');
            focusFirstElementInPanel();
        }
    
        function closeVisualSearchPanel() {
            $visualSearchPanel.removeClass(VS_PANEL_OPEN_STATE_CLASS);
            $visualSearchButton.attr('aria-expanded', 'false');
            $visualSearchButton.focus();
            resetDropZoneUI();
            pendingOriginalFileName = null;
        }

        function resetDropZoneUI() {
            if (window.VisualSearchCropper) {
                window.VisualSearchCropper.destroy();
            }
            $cropState.addClass('u-hide').removeClass('site-search__visual-crop--searching');
            $legalCopy.show();
            $cropCanvas.css({ height: '', width: '' });
            $cropImage.attr('src', '').css('height', '');
            $cropError.text('').hide();
            enableCroppedSubmitButton($cropSubmit);
            $allDropZoneElements.show();
            $visualSearchDropZone.show().removeClass('is-dragover is-error is-processing');
            $urlInputField.show();
            setSubmitDisabled(false);
            $urlInputUpload.val('').attr('aria-invalid', 'false');
        }
    
        function focusFirstElementInPanel() {
            const $focusable = Keyboard.findFocusable($visualSearchPanel);
            if ($focusable.length) {
                $focusable.eq(0).focus();
            }
        }
        
        function showDropZoneError(message, heading = 'Error', details = null) {
            const $errorHeading = $('.site-search__visual-dropzone-error-heading');
            const $errorMessageArea = $('.site-search__visual-dropzone-error-message');
            
            $errorHeading.text(heading);
            $errorMessageArea.text(message);

            if (details) {
                $errorMessageArea.append($('<p>').addClass('u-text--sm u-color--grey u-margin-t--sm').text(`Details: ${details}`));
            }
            
            $visualSearchDropZone.addClass('is-error');
            // Restore drop-zone visibility if hidden during processing
            $allDropZoneElements.show();
            $visualSearchDropZone.show().removeClass('is-processing');
        }

        function showImageSearchError(message, details) {
            const finalMessage = message || `Image search failed, error=${ImageSearchErrorCode.UNKNOWN}`;
            if (details) {
                console.error('Image search error details:', details);
            }
            $cropState.addClass('u-hide').removeClass('site-search__visual-crop--searching');
            $legalCopy.show();
            showDropZoneError(finalMessage, 'Error', details);
        }
    
        function normalizeUrl(value) {
            if (!/^https?:\/\//i.test(value)) {
                return `https://${value}`;
            }
            return value;
        }
    
        function isValidUrl(value) {
            try {
                const url = new URL(value);
                return url.hostname.includes('.');
            } catch {
                return false;
            }
        }
    }

});



// WEBPACK FOOTER //
// ./styleguide/src/assets/abtests/ab553.js